import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import DeviceTypes from '@polestar/component-warehouse-react/tokens/device-types/DeviceTypes';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import IconButton, { IconButtonVariants, } from '@polestar/component-warehouse-react/components/icon-button/IconButton';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Slider from '@polestar/component-warehouse-react/components/slider/Slider';
import WithContextMenu from '@polestar/component-warehouse-react/components/with-context-menu/WithContextMenu';
import Positions from '@polestar/component-warehouse-react/tokens/positions/Positions';
import { useVideoPlayer } from './VideoPlayerContext';
import { setSelectSubtitle } from '@polestar/component-warehouse-react/components/video-player/VideoPlayer.helpers';
import { clamp } from '@polestar/component-warehouse-react/helpers/mathematical';
import { m as motion, useMotionValue } from 'framer-motion';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import Direction from '@polestar/component-warehouse-react/components/direction/Direction';
import Directions from '@polestar/component-warehouse-react/tokens/directions/Directions';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
import { DarkThemeProvider } from '@polestar/component-warehouse-react/components/theme-providers/ThemeProviders';
import TimeCode from '@polestar/component-warehouse-react/components/time-code/TimeCode';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { useFocusInitial } from '@polestar/component-warehouse-react/hooks/focus';
import { useInteractionMode } from '@polestar/component-warehouse-react/contexts/interactionMode';
import Seekbar from '@polestar/component-warehouse-react/components/video-player/internal/Seekbar';
import { preventCancelableDefault } from '@polestar/component-warehouse-react/helpers/event';
// values in seconds
const SEEKBAR_SMALL_JUMP = 5;
const SEEKBAR_BIG_JUMP = 60;
const seekBarKeys = ['ArrowLeft', 'ArrowRight', 'ArrowTop', 'ArrowDown', 'PageUp', 'PageDown'];
const VideoPlayerControls = ({ visible = true, sizeVariant = DeviceTypes.desktop, backButtonLabel, onShowControls, menu, subtitleLabel, subtitleOffLabel, ariaLabels, onTouchStart, onMouseDown, ...props }) => {
    const internalRef = useRef(null);
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);
    const [displayedTime, setDisplayedTime] = useState(0);
    const bufferPercentage = useMotionValue(0);
    const [isDragging, setIsDragging] = useState(false);
    const [updateSeekbar, setUpdateSeekbar] = useState(false);
    // Steal focus from video element when showing controls
    const { isKeyboardControlled } = useInteractionMode();
    useFocusInitial(internalRef, visible && isKeyboardControlled);
    const { videoPlayerRef, fullscreen, setFullscreen, playing, togglePlaying, canPlay, muted, setMuted, volume, setVolume, duration, ended, setMenuOpen, availableSubtitles, selectedSubtitle, setSelectedSubtitle, currentTime, playerRef, } = useVideoPlayer();
    const handleVolumeSliderChange = useCallback((value) => {
        setVolume(value);
    }, [setVolume]);
    /**
     * Progress
     */
    useEffect(() => {
        if (visible && !isDragging) {
            setDisplayedTime(currentTime.get());
            return currentTime.on('change', (currentTime) => {
                setDisplayedTime(currentTime);
            });
        }
    }, [isDragging, currentTime, visible]);
    /**
     * Buffer
     */
    useEffect(() => {
        const updateBuffer = () => {
            const source = videoPlayerRef === null || videoPlayerRef === void 0 ? void 0 : videoPlayerRef.current;
            if (!source) {
                return [];
            }
            const buffered = [];
            for (let i = 0; i < source.buffered.length; i++) {
                buffered.push([source.buffered.start(i), source.buffered.end(i)]);
            }
            const firstBuffer = buffered.find(([start, end]) => {
                return start <= source.currentTime && end >= source.currentTime;
            });
            const newBufferPercentage = clamp((firstBuffer === null || firstBuffer === void 0 ? void 0 : firstBuffer[1]) && source.duration ? firstBuffer[1] / source.duration : 0, 0, 1);
            bufferPercentage.set(newBufferPercentage);
        };
        if (videoPlayerRef.current) {
            const video = videoPlayerRef.current;
            video.addEventListener('timeupdate', updateBuffer);
            return () => {
                video.removeEventListener('timeupdate', updateBuffer);
            };
        }
    }, [videoPlayerRef, bufferPercentage]);
    const handleVolumeKeyDown = useCallback((event) => {
        switch (event.key) {
            case 'ArrowUp':
                preventCancelableDefault(event);
                handleVolumeSliderChange(Math.min(volume + 0.1, 1));
                break;
            case 'PageUp':
                preventCancelableDefault(event);
                handleVolumeSliderChange(1);
                break;
            case 'ArrowDown':
                preventCancelableDefault(event);
                handleVolumeSliderChange(Math.max(volume - 0.1, 0));
                break;
            case 'PageDown':
                preventCancelableDefault(event);
                handleVolumeSliderChange(0);
                break;
        }
    }, [handleVolumeSliderChange, volume]);
    /**
     * Menu
     */
    const videoPlayerMenuRef = useCallback((node) => {
        setMenuOpen(!!node);
    }, [setMenuOpen]);
    const combinedMenu = useMemo(() => {
        const handleSubtitleSelect = (index) => () => {
            var _a;
            setSelectedSubtitle(index);
            if ((_a = videoPlayerRef === null || videoPlayerRef === void 0 ? void 0 : videoPlayerRef.current) === null || _a === void 0 ? void 0 : _a.textTracks) {
                setSelectSubtitle(videoPlayerRef.current.textTracks, index);
            }
        };
        return [
            ...(menu || []),
            ...(availableSubtitles.length > 0
                ? [
                    {
                        label: subtitleLabel,
                        items: [
                            {
                                label: subtitleOffLabel,
                                language: '',
                                mode: 'hidden',
                                index: -1,
                                active: selectedSubtitle === -1,
                                onClick: handleSubtitleSelect(-1),
                            },
                            ...availableSubtitles.map(({ label }, i) => {
                                return {
                                    label,
                                    active: i === selectedSubtitle,
                                    onClick: handleSubtitleSelect(i),
                                };
                            }),
                        ],
                    },
                ]
                : []),
        ];
    }, [
        menu,
        availableSubtitles,
        subtitleOffLabel,
        subtitleLabel,
        selectedSubtitle,
        setSelectedSubtitle,
        videoPlayerRef,
    ]);
    const { direction, isRTL } = useDirection();
    const menuButtonAriaLabel = ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.menu;
    const menuButton = useMemo(() => (React.createElement(VideoPlayerMenuButton, { icon: Icons.menuDotted, size: Sizes.large, variant: IconButtonVariants.transparent, ...aria({
            label: menuButtonAriaLabel,
        }) })), [menuButtonAriaLabel]);
    // force visible on open
    const videoPlayerMenu = (combinedMenu === null || combinedMenu === void 0 ? void 0 : combinedMenu.length) > 0 ? (React.createElement(WithContextMenu, { ref: videoPlayerMenuRef, key: visible ? 'show' : 'hide', items: combinedMenu, preferredPlacement: isRTL ? Positions.topRight : Positions.topLeft, backButtonLabel: backButtonLabel, attachIn: fullscreen ? playerRef.current : undefined }, menuButton)) : null;
    const controlsSpacing = (React.createElement(Spacer, { spacing: {
            mobile: Spacings.xSmall,
            desktop: Spacings.medium,
        }, orientation: Orientations.horizontal }));
    const handleFullscreenToggle = useCallback(() => {
        setFullscreen((previousValue) => !previousValue);
    }, [setFullscreen]);
    /**
     * Seekbar
     */
    const handleSeekbarInput = useCallback((newValue) => {
        // Force controls shown / reset timer
        onShowControls === null || onShowControls === void 0 ? void 0 : onShowControls();
        setIsDragging(true);
        setDisplayedTime(newValue);
        // Clicking on seekbar should start playing again
        if (ended) {
            togglePlaying();
        }
        if (videoPlayerRef.current) {
            // Have to update the internal times here also, otherwise you see a "back and forth" while it's loading the new currentTime
            setDisplayedTime(newValue);
            setUpdateSeekbar(true);
            currentTime.set(newValue);
            videoPlayerRef.current.currentTime = newValue;
        }
    }, [setIsDragging, onShowControls, currentTime, videoPlayerRef, ended, togglePlaying]);
    // prevent video seekbar out-of-sync when switching tabs
    useEffect(() => {
        const handleVisibilityChange = () => {
            setUpdateSeekbar(true);
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);
    const wasPlaying = useRef(false);
    const handleSeekbarChange = useCallback(() => {
        setIsDragging(false);
    }, []);
    const handleSeekbarKeyUp = useCallback((event) => {
        if (event.key === ' ') {
            togglePlaying();
        }
        if (seekBarKeys.includes(event.key)) {
            setIsDragging(false);
        }
    }, [togglePlaying]);
    // Disable playing until user lets go
    useEffect(() => {
        if (isDragging && playing) {
            wasPlaying.current = true;
            togglePlaying();
        }
        if (wasPlaying.current && !isDragging) {
            togglePlaying();
            wasPlaying.current = false;
        }
    }, [isDragging, playing, togglePlaying]);
    /**
     * YouTube like video controls, 5s for arrow keys, 60s for page keys
     */
    const handleSeekbarKeyDown = useCallback((event) => {
        var _a;
        if (seekBarKeys.includes(event.key)) {
            preventCancelableDefault(event);
            setIsDragging(true);
            const currentTime = ((_a = videoPlayerRef === null || videoPlayerRef === void 0 ? void 0 : videoPlayerRef.current) === null || _a === void 0 ? void 0 : _a.currentTime) || 0;
            switch (event.key) {
                case 'ArrowUp':
                case 'ArrowRight':
                    handleSeekbarInput(currentTime + SEEKBAR_SMALL_JUMP);
                    break;
                case 'ArrowDown':
                case 'ArrowLeft':
                    handleSeekbarInput(currentTime - SEEKBAR_SMALL_JUMP);
                    break;
                case 'PageUp':
                    handleSeekbarInput(currentTime + SEEKBAR_BIG_JUMP);
                    break;
                case 'PageDown':
                    handleSeekbarInput(currentTime - SEEKBAR_BIG_JUMP);
                    break;
            }
        }
    }, [videoPlayerRef, handleSeekbarInput]);
    /**
     * Sound
     */
    const handleSoundToggle = useCallback(() => {
        setMuted((previousValue) => !previousValue);
    }, [setMuted]);
    const handleSoundMouseEnter = useCallback(() => {
        setShowVolumeSlider(true);
    }, [setShowVolumeSlider]);
    const handleSoundMouseLeave = useCallback(() => {
        setShowVolumeSlider(false);
    }, [setShowVolumeSlider]);
    const handleSoundFocus = useCallback(() => {
        setShowVolumeSlider(true);
    }, [setShowVolumeSlider]);
    const handleSoundBlur = useCallback(() => {
        setShowVolumeSlider(false);
    }, [setShowVolumeSlider]);
    useEffect(() => {
        if (updateSeekbar) {
            const timeout = setTimeout(() => {
                setUpdateSeekbar(false);
            }, 0);
            return () => {
                clearTimeout(timeout);
            };
        }
    }, [updateSeekbar]);
    const handleMouseDown = useCallback((e) => {
        e.stopPropagation();
        onMouseDown === null || onMouseDown === void 0 ? void 0 : onMouseDown(e);
    }, [onMouseDown]);
    const handleTouchStart = useCallback((e) => {
        e.stopPropagation();
        onTouchStart === null || onTouchStart === void 0 ? void 0 : onTouchStart(e);
    }, [onTouchStart]);
    return (
    // @ts-ignore
    React.createElement(Container, { ref: internalRef, sizeVariant: sizeVariant, visible: visible, isKeyboardControlled: isKeyboardControlled, ...props, dir: Directions.leftToRight, onMouseDown: handleMouseDown, onTouchStart: handleTouchStart },
        React.createElement(ControlsBackground, null),
        React.createElement(DarkThemeProvider, null,
            React.createElement(PlayButton, { onClick: togglePlaying, icon: ended ? Icons.replay : playing ? Icons.pause : Icons.playFilled, size: Sizes.large, variant: IconButtonVariants.transparent, ...aria({
                    label: ended ? ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.replay : playing ? ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.pause : ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.play,
                }) }),
            controlsSpacing,
            React.createElement(SeekbarWrapper, null,
                React.createElement(BufferBar, { "data-type": "buffer", style: { transformOrigin: 0, scaleX: bufferPercentage, translateY: '-50%' } }),
                React.createElement(Seekbar, { min: 0, max: duration, onInput: handleSeekbarInput, onChange: handleSeekbarChange, handleProps: {
                        onKeyUp: handleSeekbarKeyUp,
                        onKeyDown: handleSeekbarKeyDown,
                        'aria-label': ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.progress,
                    }, isDragging: isDragging, updateSeekbar: updateSeekbar, canPlay: canPlay, displayedTime: displayedTime, playing: playing, videoPlayerRef: videoPlayerRef, duration: duration })),
            controlsSpacing,
            sizeVariant !== DeviceTypes.mobile && !!duration && (React.createElement(React.Fragment, null,
                React.createElement(StyledTimeCode, { duration: duration, currentTime: displayedTime }),
                controlsSpacing)),
            React.createElement(SoundControlsContainer, { onMouseEnter: handleSoundMouseEnter, onMouseLeave: handleSoundMouseLeave, onFocus: handleSoundFocus, onBlur: handleSoundBlur, onKeyDown: handleVolumeKeyDown },
                showVolumeSlider && !muted && (React.createElement(VolumeSlider, { min: 0, max: 1, value: muted ? 0 : volume, onChange: handleVolumeSliderChange, orientation: Orientations.vertical, handleProps: { tabIndex: -1 } })),
                React.createElement(SoundControlButton, { onClick: handleSoundToggle, icon: muted || volume === 0 ? Icons.volumeOff : Icons.volumeOn, size: Sizes.large, variant: IconButtonVariants.transparent, ...aria({
                        label: muted ? ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.unmute : ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.mute,
                    }) }))),
        React.createElement(Direction, { dir: direction }, videoPlayerMenu),
        React.createElement(DarkThemeProvider, null,
            React.createElement(FullscreenButton, { onClick: handleFullscreenToggle, icon: fullscreen ? Icons.minimize : Icons.maximize, size: Sizes.large, variant: IconButtonVariants.transparent, ...aria({
                    label: fullscreen ? ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.exitFullscreen : ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.fullscreen,
                }) }))));
};
export default VideoPlayerControls;
const Container = motion(styled(Direction)(({ theme, sizeVariant, visible, isKeyboardControlled }) => css(css `
        position: absolute;
        inset-block-end: 0;
        inset-inline-start: 0;
        inset-inline-end: 0;
        display: flex;
        visibility: ${visible ? 'visible' : 'hidden'};
        opacity: ${visible ? 1 : 0};
        pointer-events: ${visible ? 'auto' : 'none'};
        transform: ${visible ? 'none' : 'translate(0, 30%)'};
        transition:
          ${createCSSTransition('opacity', MotionEasings.default, MotionDurations.slow)},
          ${createCSSTransition('visibility', MotionEasings.default, MotionDurations.slow)},
          ${createCSSTransition('transform', MotionEasings.default, MotionDurations.slow)};
        flex-direction: row;
        justify-content: center;
        align-items: center;
        z-index: 2;
        padding: ${theme.videoPlayer.padding[sizeVariant]};
      `, isKeyboardControlled &&
    css `
          transition-duration: 0s;
        `)));
const ControlsBackground = styled.div(({ theme }) => css `
    background-image: linear-gradient(
      to bottom,
      ${theme.videoPlayer.controls.backgroundColor.from},
      ${theme.videoPlayer.controls.backgroundColor.to}
    );
    pointer-events: none;
    position: absolute;
    inset-block-start: 0;
    inset-block-end: 0;
    inset-inline-start: 0;
    inset-inline-end: 0;
    z-index: -1;
  `);
const ControlButton = styled(IconButton)(({ theme }) => css `
    color: ${theme.videoPlayer.color};

    &:hover {
      color: ${theme.videoPlayer.color};
    }
  `);
const PlayButton = styled(ControlButton) ``;
const FullscreenButton = styled(ControlButton) ``;
const SoundControlsContainer = styled.div `
  position: relative;
`;
const SoundControlButton = styled(ControlButton) ``;
const VolumeSlider = styled(Slider) `
  height: 50px;
  position: absolute;
  inset-block-end: 100%;
  inset-inline-start: 50%;
  transform: translateX(-50%);
  z-index: -2;
`;
const VideoPlayerMenuButton = styled(ControlButton) ``;
const SeekbarWrapper = styled.div `
  flex: 1;
  position: relative;
`;
const BufferBar = styled(motion.div)(({ theme }) => css `
    position: absolute;
    inset-block-start: 50%;
    inset-inline-start: 0;
    height: 2px;
    width: 100%;
    background: ${theme.videoPlayer.bufferBar.backgroundColor};
  `);
const StyledTimeCode = styled(TimeCode)(({ theme }) => css `
    color: ${theme.videoPlayer.color};
  `);
